.landingPage {
  width: 100%;
  // clip, not hidden: hidden makes this a scroll container that phones can pan sideways into the
  // hero diagram's bleed.
  overflow-x: clip;
  padding-top: 0; /* Hero handles its own padding now */
  background: var(--color-background);
}

.section {
  padding: 8rem 2rem;
  position: relative;

  /* Alternating section backgrounds */
  &:nth-child(even) {
    background: linear-gradient(180deg, var(--color-surface-raised), var(--color-surface-subtle));
  }

  &:nth-child(odd) {
    background: var(--color-background);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80%;
    height: 1px;
    background: radial-gradient(circle, var(--color-border) 0%, transparent 100%);
    opacity: 0.5;
  }
}

.container {
  max-width: 1300px; /* Increased max-width */
  margin: 0 auto;
  width: 100%;
}

.sectionHeader {
  text-align: center;
  margin-bottom: 5rem;
  max-width: 800px;
  margin-inline-start: auto;
  margin-inline-end: auto;
}

.sectionTitle {
  font-size: clamp(2.5rem, 5vw, 3.5rem);
  font-weight: 700;
  text-align: center;
  color: var(--color-foreground);
  margin: 0 0 1.5rem 0;
  letter-spacing: -0.02em;
  line-height: 1.1;
}

.gradient {
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
}

.sectionSubtitle {
  font-size: 1.25rem;
  text-align: center;
  color: var(--color-muted);
  margin: 0 auto;
  line-height: 1.6;
  max-width: 600px;
}

.features {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 350px), 1fr));
  gap: 2.5rem;
}

.feature {
  background: var(--color-card);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  padding: 3rem 2.5rem;
  transition: all 0.4s cubic-bezier(0.2, 0.8, 0.2, 1);
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    inset-inline-start: 0;
    inset-inline-end: 0;
    height: 100%;
    background: radial-gradient(circle at top right, var(--color-primary-soft), transparent 40%);
    opacity: 0;
    transition: opacity 0.4s ease;
  }

  &:hover {
    border-color: var(--color-primary-border);

    &::before {
      opacity: 1;
    }

    .featureIcon {
      color: var(--color-primary);
    }
  }
}

.featureIcon {
  font-size: 3.5rem;
  margin-bottom: 1.5rem;
  display: inline-block;
  color: var(--color-foreground);
  transition: all 0.3s ease;
}

.featureTitle {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-foreground);
  margin: 0 0 1rem 0;
}

.featureDescription {
  font-size: 1.05rem;
  line-height: 1.6;
  color: var(--color-muted);
  margin: 0;
}

.statsSection {
  padding: 6rem 2rem;
  background: linear-gradient(135deg, var(--color-primary-soft), var(--color-accent-soft));
  border-top: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  position: relative;
  overflow: hidden;
}

.stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 4rem;
  text-align: center;
  position: relative;
  z-index: 1;
}

.stat {
  padding: 2rem;
}

.statValue {
  font-family: var(--font-display-family);
  font-size: clamp(3rem, 6vw, 5rem);
  font-weight: 700;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.5rem;
  letter-spacing: -0.03em;
}

.statLabel {
  font-family: var(--font-label-family);
  font-size: 0.8125rem;
  color: var(--color-muted);
  font-weight: var(--font-label-weight);
  text-transform: uppercase;
  letter-spacing: var(--tracking-label);
}

.ctaSection {
  padding: 10rem 2rem;
  background: var(--color-background);
  position: relative;
  overflow: hidden;
  text-align: center;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 800px;
    height: 800px;
    background: radial-gradient(circle, var(--color-primary-soft) 0%, transparent 70%);
    pointer-events: none;
    z-index: 0;
  }
}

.ctaContent {
  position: relative;
  z-index: 1;
  max-width: 800px;
  margin: 0 auto;
}

.ctaTitle {
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 700;
  color: var(--color-foreground);
  margin: 0 0 1.5rem 0;
  line-height: 1.1;
}

.ctaSubtitle {
  font-size: 1.35rem;
  color: var(--color-muted);
  margin: 0 0 3rem 0;
  font-weight: 400;
}

.ctaActions {
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  flex-wrap: wrap;
}

.ctaButton,
.ctaButtonSecondary {
  padding: 1.25rem 3rem;
  border-radius: 0.75rem;
  font-size: 1.125rem;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  span {
    position: relative;
    z-index: 1;
  }
}

.ctaButton {
  background: var(--color-primary);
  color: var(--color-primary-foreground);
  box-shadow: 0 4px 20px color-mix(in srgb, var(--color-primary) 24%, transparent);

  &:hover {
    background: var(--color-action-hover);
  }
}

.ctaButtonSecondary {
  background: transparent;
  color: var(--color-foreground);
  border: 2px solid var(--color-border);

  &:hover {
    border-color: var(--color-primary);
    background: var(--color-surface-raised);
  }
}

@media (max-width: 768px) {
  .section {
    padding: 5rem 1.5rem;
  }

  .features {
    grid-template-columns: 1fr;
  }

  .stats {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }

  .statValue {
    font-size: 2.5rem;
  }

  .ctaActions {
    flex-direction: column;
    align-items: stretch;

    a {
      width: 100%;
    }
  }
}

@media (max-width: 480px) {
  .section,
  .statsSection,
  .ctaSection {
    padding-inline-end: 1rem;
    padding-inline-start: 1rem;
  }

  .feature,
  .stat {
    padding-inline-end: 1.25rem;
    padding-inline-start: 1.25rem;
  }

  .stats {
    gap: 1rem;
  }
}
